import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../config/api';

const AuthContext = createContext(null);

// Features and which plans can access them
const FEATURE_ACCESS = {
  opportunities: ['monthly', 'annual'],
  trades: ['monthly', 'annual'],
  notifications: ['monthly', 'annual'],
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [plan, setPlan] = useState('free');
  const [graceActive, setGraceActive] = useState(false);
  const [graceEndsAt, setGraceEndsAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyPlanState = useCallback((userData) => {
    const p = userData.plan || 'free';
    const grace = userData.grace_period_ends_at
      ? new Date() < new Date(userData.grace_period_ends_at)
      : false;
    setPlan(p);
    setGraceActive(grace);
    setGraceEndsAt(userData.grace_period_ends_at || null);
  }, []);

  /**
   * Refresh plan status from the server (call after checkout success).
   */
  const refreshPlan = useCallback(async (googleId) => {
    if (!googleId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscriptions/status`, {
        headers: { 'x-user-id': googleId },
      });
      const data = await res.json();
      if (data.success) {
        const { plan: newPlan, gracePeriodEndsAt, graceActive: ga } = data.data;
        setPlan(newPlan);
        setGraceActive(ga);
        setGraceEndsAt(gracePeriodEndsAt);
        // Persist updated plan in localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          const updated = { ...parsed, plan: newPlan, grace_period_ends_at: gracePeriodEndsAt };
          localStorage.setItem('user', JSON.stringify(updated));
          setUser(updated);
        }
      }
    } catch (err) {
      console.error('Failed to refresh plan:', err);
    }
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        applyPlanState(parsed);
        refreshPlan(parsed.sub);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, [applyPlanState, refreshPlan]);

  // Intercept fetch to automatically add x-user-id header to plan-protected routes
  useEffect(() => {
    const original = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      const url = typeof input === 'string' ? input
        : input instanceof Request ? input.url : String(input);
      if (/\/api\/(trades|opportunities|notifications|positions|trade-position-jobs|position-jobs|open-position-simulated|close-position-simulated|hedge-strategies|calculate-hedge)/.test(url)) {
        const stored = localStorage.getItem('user');
        const googleId = stored ? JSON.parse(stored)?.sub : null;
        if (googleId) {
          init = { ...init, headers: { 'x-user-id': googleId, ...init.headers } };
        }
      }
      return original(input, init);
    };
    return () => { window.fetch = original; };
  }, []);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    applyPlanState(userData);
  };

  const logout = () => {
    setUser(null);
    setPlan('free');
    setGraceActive(false);
    setGraceEndsAt(null);
    localStorage.removeItem('user');
  };

  const canAccess = useCallback((feature) => {
    if (graceActive) return true;
    const allowed = FEATURE_ACCESS[feature];
    if (!allowed) return true; // unknown feature — allow
    return allowed.includes(plan);
  }, [graceActive, plan]);

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    loading,
    plan,
    graceActive,
    graceEndsAt,
    canAccess,
    refreshPlan,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
